import { Box, VStack, Text, HStack } from '@chakra-ui/react';
import { PageContext } from 'components/common/PageContext';
import { Worksheet } from 'hooks/api/backend/worksheets/useWorksheets';
import React, { ReactElement, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { setWorksheetDashboardReturnParams } from 'utils/worksheetDashboardReturnParams';
import { WorksheetsTab } from './worksheetDashboard/WorksheetsTab';
import { ClassesTab } from './worksheetDashboard/ClassesTab';
import { GuestStudentsTab } from './worksheetDashboard/GuestStudentsTab';

export enum WorksheetDashboardTab {
  WORKSHEETS = 'worksheets',
  CLASSES = 'classes',
  STUDENTS = 'students',
}

export type WorksheetData = Worksheet & {
  actionItem: ReactElement;
  participants?: string;
};

export const WorksheetDashboard = (): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize activeTab from URL or default to WORKSHEETS
  const tabFromUrl = searchParams.get('t') as WorksheetDashboardTab;
  const initialTab = Object.values(WorksheetDashboardTab).includes(tabFromUrl)
    ? tabFromUrl
    : WorksheetDashboardTab.WORKSHEETS;
  const [activeTab, setActiveTab] = useState<WorksheetDashboardTab>(initialTab);

  useEffect(() => {
    const urlTab = searchParams.get('t') as WorksheetDashboardTab;
    const validUrlTab =
      urlTab && Object.values(WorksheetDashboardTab).includes(urlTab)
        ? urlTab
        : WorksheetDashboardTab.WORKSHEETS;

    if (validUrlTab !== activeTab) {
      setActiveTab(validUrlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Persist current params so breadcrumbs on child pages can link back here with same state
  useEffect(() => {
    setWorksheetDashboardReturnParams(searchParams);
  }, [searchParams]);

  const handleTabChange = (tab: WorksheetDashboardTab): void => {
    setActiveTab(tab);

    const newParams = new URLSearchParams();
    newParams.set('t', tab);
    // Clear filter-related params when switching tabs
    newParams.delete('s');
    newParams.delete('f');
    newParams.delete('p');
    setSearchParams(newParams, { replace: true });
  };

  const tabs = [
    { id: WorksheetDashboardTab.WORKSHEETS, label: 'Arbeitsblätter' },
    { id: WorksheetDashboardTab.CLASSES, label: 'Kurse' },
    { id: WorksheetDashboardTab.STUDENTS, label: 'Lernende' },
  ];

  const renderTabContent = (): JSX.Element | null => {
    switch (activeTab) {
      case WorksheetDashboardTab.WORKSHEETS:
        return <WorksheetsTab searchParams={searchParams} setSearchParams={setSearchParams} />;
      case WorksheetDashboardTab.CLASSES:
        return <ClassesTab searchParams={searchParams} setSearchParams={setSearchParams} />;
      case WorksheetDashboardTab.STUDENTS:
        return <GuestStudentsTab searchParams={searchParams} setSearchParams={setSearchParams} />;
      default:
        return null;
    }
  };

  const renderTabs = (): JSX.Element => {
    return (
      <Box px="l">
        <HStack gap="0" align="stretch" borderBottom="1px solid" borderColor="border.accent">
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <Box
                key={tab.id}
                as="button"
                onClick={() => handleTabChange(tab.id)}
                width="140px"
                cursor="pointer"
                position="relative"
                pb="10px"
                _hover={{
                  opacity: isActive ? 1 : 0.7,
                }}
                transition="opacity 0.2s"
              >
                <VStack gap="10px" align="center">
                  <Text
                    textStyle="body.s.medium"
                    color={isActive ? 'border.accent.active' : 'text.tertiary'}
                  >
                    {tab.label}
                  </Text>
                </VStack>
                {isActive && (
                  <Box
                    position="absolute"
                    left="0"
                    right="0"
                    bottom="-1px"
                    height="2px"
                    bg="background.accent"
                    borderRadius="1px"
                  />
                )}
              </Box>
            );
          })}
        </HStack>
      </Box>
    );
  };

  return (
    <PageContext title="Arbeitsblätter" hasBackButton headTitle="Arbeitsblätter">
      <Box
        height="100%"
        minHeight="100%"
        flexDirection="column"
        maxWidth="100%"
        flexGrow={1}
        width="100%"
        margin={{ md: '40px 0' }}
      >
        <VStack
          bg="background.canvas"
          align="left"
          p="0"
          gap="32px"
          width="100%"
          borderRadius={{ md: '10px' }}
          overflow="hidden"
        >
          {/* Header with Tabs */}
          <VStack
            gap="l"
            align="stretch"
            pt="l"
            pb="0"
            borderTopRadius="10px"
            bg="background.primary"
          >
            {/* Title */}
            <Box px="l">
              <Text textStyle="h3.medium" color="text.primary">
                Arbeitsblätter und Kurse
              </Text>
              <Text textStyle="body.s.regular" color="text.secondary">
                Hier findest du alle Arbeitsblätter, Kurse und Lernende – und kannst Arbeitsblätter
                direkt an Kurse oder einzelne Lernende verteilen.
              </Text>
            </Box>

            {/* Tabs */}
            {renderTabs()}
          </VStack>

          {/* Content Area */}
          <Box px="l" py="l" bg="background.primary" borderRadius={{ md: '10px' }} width="100%">
            {renderTabContent()}
          </Box>
        </VStack>
      </Box>
    </PageContext>
  );
};
